import type { ISendPasswordResetCmd } from "@/data/commands/auth/SendPasswordResetCmd";
import type { IUsernamePasswordAuthCmd } from "@/data/commands/auth/UsernamePasswordAuthCmd";
import { Factory } from "@/data/dao/Factory";
import { AuthenticationDao } from "@/data/dao/service/AuthenticationDao";
import type { DaoError } from "@/data/dao/service/BaseServiceDao";
import { exceptionTypes } from "@/data/util/Config";
import { Validation } from "@/data/util/Validation";

import { logError } from "@/shared/logging/errors";

import { i18n } from "@/portal/Trans";
import { UiNotification } from "@/portal/ui/Notification";
import { LocationSwitch } from "@/portal/ui/RestaurantSwitch";

export class LoginController {
    private $element: JQuery;
    private $loginForm: JQuery;

    constructor($element: JQuery) {
        this.$element = $element;
        this.$loginForm = this.$element.find("#kt_login_form");
        this.showLogin();
    }

    private onLoginFormSubmit() {
        const loginBtn = this.$loginForm.find("#kt_login_signin_submit");
        if (loginBtn.is(":disabled")) return;

        this.$loginForm.find("#password-error, #username-error, > .alert").remove();
        const nameField = this.$loginForm.find('input[name="username"]');
        const passwordField = this.$loginForm.find('input[name="password"]');

        const dto: IUsernamePasswordAuthCmd = {
            userName: nameField.val().length === 0 ? undefined : nameField.val(),
            password: passwordField.val().length === 0 ? undefined : passwordField.val(),
            scope: "portal",
        };

        let hasError = false;
        this.$loginForm.find(".is-invalid").removeClass("is-invalid");

        if (dto.userName === undefined || !Validation.isEmail(dto.userName)) {
            nameField.addClass("is-invalid").parent().addClass("is-invalid");
            $(
                '<div id="username-error" class="error invalid-feedback">' + i18n("login.enterValidEmail") + "</div>"
            ).insertAfter(nameField);
            hasError = true;
        }

        if (dto.password === undefined || !Validation.isPassword(dto.password)) {
            passwordField.addClass("is-invalid").parent().addClass("is-invalid");
            $(
                '<div id="password-error" class="error invalid-feedback">' + i18n("login.enterValidPassword") + "</div>"
            ).insertAfter(passwordField);
            hasError = true;
        }

        if (hasError) return false;

        loginBtn.addClass("kt-spinner kt-spinner--light").prop("disabled", true);
        Factory.getInstance<AuthenticationDao>(AuthenticationDao)
            .auth(dto)
            .then(() => {
                LocationSwitch.releaseCache();

                location.href = "/";
            })
            .catch((ex) => {
                logError(ex);
                const e = ex as DaoError;
                loginBtn.removeClass("kt-spinner kt-spinner--light").prop("disabled", false);

                if (e.type === exceptionTypes.featureNotImplementedException)
                    this.setError(i18n("login.featureNotImplemented"));
                else {
                    if (e.additionalData !== undefined && e.additionalData.code == 3)
                        this.setError(i18n("login.loginFailedBlocked"));
                    else this.setError(i18n("login.loginFailedCredentials"));
                }

                passwordField.val("").focus();
            });
    }

    private onPasswordResetSubmit() {
        const loginBtn = this.$loginForm.find("#kt_login_signin_submit");
        if (loginBtn.is(":disabled")) return;

        this.$loginForm.find("#password-error, #username-error, > .alert").remove();
        const nameField = this.$loginForm.find('input[name="username"]');

        const dto: ISendPasswordResetCmd = {
            email: nameField.val().length === 0 ? undefined : nameField.val(),
        };

        let hasError = false;
        this.$loginForm.find(".is-invalid").removeClass("is-invalid");

        if (dto.email === undefined || !Validation.isEmail(dto.email)) {
            nameField.addClass("is-invalid").parent().addClass("is-invalid");
            $(
                '<div id="username-error" class="error invalid-feedback">' + i18n("login.enterValidEmail") + "</div>"
            ).insertAfter(nameField);
            hasError = true;
        }

        if (hasError) return false;

        loginBtn.addClass("kt-spinner kt-spinner--light").prop("disabled", true);
        Factory.getInstance<AuthenticationDao>(AuthenticationDao)
            .sendPasswordReset(dto)
            .then(() => {
                this.showLogin();
                loginBtn.removeClass("kt-spinner kt-spinner--light").prop("disabled", false);
                UiNotification.showSuccess(i18n("login.resetPwSuccess"), 6000);
            })
            .catch((ex) => {
                logError(ex);
                loginBtn.removeClass("kt-spinner kt-spinner--light").prop("disabled", false);
                this.setError(i18n("login.failedResetPw"));
            });
    }

    private setError(msg: string) {
        this.$loginForm.find("> .alert").remove();
        this.$loginForm.prepend(
            '<div class="alert alert-bold alert-solid-danger alert-dismissible" role="alert"><div class="alert-text">' +
                msg +
                '</div><div class="alert-close"><i class="flaticon2-cross kt-icon-sm" data-dismiss="alert"></i></div></div>'
        );
    }

    private showLogin() {
        this.$loginForm.find("#password-error, #username-error, > .alert").remove();
        this.$loginForm.find(".is-invalid").removeClass("is-invalid");
        this.$loginForm.unbind("submit").bind("submit", (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.onLoginFormSubmit();
            return false;
        });

        this.$element.find(".kt-login__form > .kt-login__title > h3").html(i18n("login.login"));
        this.$loginForm.find('input[name="password"]').parent().show();
        this.$loginForm
            .find(".kt-login__link-forgot")
            .html(i18n("login.passwordLost"))
            .unbind("click")
            .bind("click", (e) => {
                e.preventDefault();
                this.showPasswordReset();
            });

        this.$loginForm.find("#kt_login_signin_submit").html(i18n("login.signinBtn"));
    }

    private showPasswordReset() {
        this.$loginForm.find("#password-error, #username-error, > .alert").remove();
        this.$loginForm.find(".is-invalid").removeClass("is-invalid");
        this.$loginForm.unbind("submit").bind("submit", (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.onPasswordResetSubmit();
            return false;
        });

        this.$element.find(".kt-login__form > .kt-login__title > h3").html(i18n("login.resetPassword"));
        this.$loginForm.find('input[name="password"]').parent().hide();
        this.$loginForm
            .find(".kt-login__link-forgot")
            .html(i18n("login.backToLogin"))
            .unbind("click")
            .bind("click", (e) => {
                e.preventDefault();
                this.showLogin();
            });

        this.$loginForm.find("#kt_login_signin_submit").html(i18n("login.resetPasswordBtn"));
    }
}
